import React, { useState, useEffect } from "react";
import { Layout, Button, Avatar, Tag } from "antd";
import { Api } from "@model";
import { useNavigate, useParams } from "react-router-dom";
import { UserOutlined } from "@ant-design/icons";

const { Header, Content, Sider } = Layout;

const UserMessagesPage = () => {
  const { userId } = useParams<{ userId: string }>();
  const [chats, setChats] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [selectedChatType, setSelectedChatType] = useState<
    "ai" | "user" | null
  >(null);
  const [selectedChat, setSelectedChat] = useState<any | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchChats = async () => {
      if (!userId) return;
      setLoading(true);
      try {
        const response = await Api.get(
          `/api/admin/panel/users/messages/${userId}`
        );
        if (response.data && response.data.data) {
          setChats(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching chats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchChats();
  }, [userId]);

  const handleChatClick = async (chat: any) => {
    if (!userId) return;
    setSelectedChatId(chat._id);
    setSelectedChat(chat);

    let chatType: "ai" | "user" = "user";
    if (chat.toCharacterID) {
      const charId =
        typeof chat.toCharacterID === "object"
          ? chat.toCharacterID._id
          : chat.toCharacterID;
      if (charId) {
        chatType = "ai";
      }
    }
    setSelectedChatType(chatType);

    try {
      const response = await Api.get(
        `/api/admin/panel/users/messages/${userId}?chatId=${chat._id}`
      );
      if (response.data && response.data.data) {
        const chatMessages = Array.isArray(response.data.data)
          ? response.data.data
          : [];
        const sortedMessages: any[] = chatMessages.sort(
          (a: any, b: any) =>
            new Date(a.sendDate).getTime() - new Date(b.sendDate).getTime()
        );
        if (sortedMessages.some((message) => message.role === "assistant")) {
          setSelectedChatType("ai");
        }
        setMessages(sortedMessages);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
      setMessages([]);
    }
  };

  const getChatDisplayName = (chat: any) => {
    if (chat.toCharacterID) {
      if (typeof chat.toCharacterID === "object" && chat.toCharacterID._id) {
        return chat.toCharacterID.name || "Unknown Character";
      } else if (typeof chat.toCharacterID === "string") {
        return "Unknown Character";
      }
    }
    if (chat.toUserID) {
      if (typeof chat.toUserID === "object" && chat.toUserID._id) {
        return chat.toUserID.name || "Unknown User";
      } else if (typeof chat.toUserID === "string") {
        return "Unknown User";
      }
    }
    return "Unknown Chat";
  };

  const getChatAvatar = (chat: any) => {
    if (chat.toCharacterID) {
      const charId =
        typeof chat.toCharacterID === "object"
          ? chat.toCharacterID._id
          : chat.toCharacterID;
      if (charId) {
        return `/images/characters/${charId}_sm.jpg`;
      }
    }
    if (chat.toUserID) {
      const user = typeof chat.toUserID === "object" ? chat.toUserID : null;
      if (user?.avatar) {
        return user.avatar.startsWith("http")
          ? user.avatar
          : `/${user.avatar}_sm.jpg`;
      }
    }
    return null;
  };

  return (
    <Layout className="no-background" style={{ height: "100vh" }}>
      <Header
        style={{
          background: "#002140",
          padding: "0 10px",
          color: "white",
          fontSize: "18px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Button
          type="text"
          onClick={() => navigate(-1)}
          style={{ color: "white", marginRight: "10px" }}
        >
          Back
        </Button>
        <span style={{ marginLeft: "10px" }}>User Messages</span>
      </Header>
      <Layout>
        <Sider
          width={300}
          style={{
            background: "#fff",
            padding: "16px",
            boxShadow: "2px 0 5px rgba(0, 0, 0, 0.1)",
            overflowY: "auto",
          }}
        >
          <div style={{ marginBottom: "20px" }}>
            {loading ? (
              <div>Loading chats...</div>
            ) : chats.length === 0 ? (
              <div>No chats found</div>
            ) : (
              chats.map((chat, index) => (
                <div
                  key={chat._id || index}
                  style={{
                    padding: "12px",
                    marginBottom: "8px",
                    background:
                      selectedChatId === chat._id ? "#e6f7ff" : "#f5f5f5",
                    borderRadius: "8px",
                    cursor: "pointer",
                    border:
                      selectedChatId === chat._id
                        ? "2px solid #1890ff"
                        : "2px solid transparent",
                  }}
                  onClick={() => handleChatClick(chat)}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "8px",
                    }}
                  >
                    <Avatar
                      src={getChatAvatar(chat)}
                      icon={<UserOutlined />}
                      style={{ marginRight: "10px" }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: "bold" }}>
                        {getChatDisplayName(chat)}
                      </div>
                      {chat.toCharacterID &&
                        (typeof chat.toCharacterID === "object"
                          ? chat.toCharacterID._id
                          : chat.toCharacterID) && (
                          <Tag color="blue" style={{ marginTop: "4px" }}>
                            AI Chat
                          </Tag>
                        )}
                      {chat.toUserID &&
                        (typeof chat.toUserID === "object"
                          ? chat.toUserID._id
                          : chat.toUserID) &&
                        !(
                          chat.toCharacterID &&
                          (typeof chat.toCharacterID === "object"
                            ? chat.toCharacterID._id
                            : chat.toCharacterID)
                        ) && (
                          <Tag color="green" style={{ marginTop: "4px" }}>
                            User Chat
                          </Tag>
                        )}
                    </div>
                  </div>
                  {chat.lastMessageDate && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#888",
                        marginTop: "4px",
                      }}
                    >
                      {new Date(chat.lastMessageDate).toLocaleDateString()}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </Sider>
        <Content
          style={{
            padding: "16px",
            display: "flex",
            flexDirection: "column",
            height: "calc(100vh - 64px)",
            background: "#f5f5f5",
          }}
        >
          {selectedChatId ? (
            <div style={{ flexGrow: 1, overflowY: "auto", padding: "16px" }}>
              {messages.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    color: "#888",
                    marginTop: "50px",
                  }}
                >
                  No messages in this chat
                </div>
              ) : (
                messages.map((message, index) => {
                  const messageFromUserID =
                    typeof message.fromUserID === "string"
                      ? message.fromUserID
                      : message.fromUserID?._id || message.fromUserID;

                  let isFromCurrentUser = false;
                  if (selectedChatType === "ai") {
                    isFromCurrentUser = message.role === "user";
                  } else {
                    isFromCurrentUser = messageFromUserID === userId;
                  }

                  return (
                    <div
                      key={message._id || message.messageID || index}
                      style={{
                        display: "flex",
                        justifyContent: isFromCurrentUser
                          ? "flex-end"
                          : "flex-start",
                        marginBottom: "16px",
                        alignItems: "flex-start",
                      }}
                    >
                      {!isFromCurrentUser && (
                        <Avatar
                          src={
                            selectedChatType === "ai" &&
                            selectedChat?.toCharacterID
                              ? `/images/characters/${
                                  typeof selectedChat.toCharacterID === "object"
                                    ? selectedChat.toCharacterID._id
                                    : selectedChat.toCharacterID
                                }_sm.jpg`
                              : selectedChatType === "user" &&
                                selectedChat?.toUserID &&
                                typeof selectedChat.toUserID === "object"
                              ? selectedChat.toUserID.avatar?.startsWith("http")
                                ? selectedChat.toUserID.avatar
                                : `/${selectedChat.toUserID.avatar}_sm.jpg`
                              : null
                          }
                          icon={<UserOutlined />}
                          style={{ marginRight: "8px", marginTop: "4px" }}
                        />
                      )}
                      <div
                        style={{
                          maxWidth: "70%",
                          padding: "10px 15px",
                          borderRadius: "20px",
                          backgroundColor: isFromCurrentUser
                            ? "#1890ff"
                            : "#f0f0f0",
                          color: isFromCurrentUser ? "white" : "black",
                          textAlign: isFromCurrentUser ? "right" : "left",
                          boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
                          wordWrap: "break-word",
                        }}
                      >
                        <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                          {message.message || message.text || ""}
                        </p>
                        <small
                          style={{
                            display: "block",
                            marginTop: 8,
                            color: isFromCurrentUser ? "#d1eaff" : "#888",
                          }}
                        >
                          {message.sendDate
                            ? new Date(message.sendDate).toLocaleString()
                            : ""}
                        </small>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            <div
              style={{ textAlign: "center", color: "#888", marginTop: "50px" }}
            >
              Select a chat to view messages
            </div>
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

export default UserMessagesPage;
